/**
 * Data layer for `aws-s3-data-export-integration`. Thin typed fetch wrappers
 * over /api/v1/aws-s3-data-export-integration/{destinations,jobs}.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface DestinationRow {
	id: string;
	bucket: string;
	prefix: string;
	region: string;
	access_key_id_last4: string;
	status: string;
	created_at: number;
	updated_at: number;
}

export interface JobRow {
	id: string;
	tenant_id: string;
	destination_id: string;
	idempotency_key: string;
	dataset: string;
	range_start: number;
	range_end: number;
	status: string;
	rows_exported: number;
	error: string | null;
	created_at: number;
	updated_at: number;
}

export interface ListDestinationsResponse {
	items: DestinationRow[];
}

export interface ListJobsResponse {
	items: JobRow[];
}

export interface CreateDestinationBody {
	bucket: string;
	prefix?: string;
	region: string;
	access_key_id: string;
	secret_key: string;
}

export interface CreateJobBody {
	destination_id: string;
	idempotency_key: string;
	dataset: string;
	range_start: number;
	range_end: number;
}

const BASE = "/api/v1/aws-s3-data-export-integration";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listDestinations(): Promise<ListDestinationsResponse> {
	const res = await fetch(`${BASE}/destinations`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListDestinationsResponse>(res);
}

export async function createDestination(
	body: CreateDestinationBody,
): Promise<{ destination: DestinationRow }> {
	const res = await fetch(`${BASE}/destinations`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<{ destination: DestinationRow }>(res);
}

export async function listJobs(status?: string): Promise<ListJobsResponse> {
	const qs = status ? `?status=${encodeURIComponent(status)}` : "";
	const res = await fetch(`${BASE}/jobs${qs}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListJobsResponse>(res);
}

export async function createJob(body: CreateJobBody): Promise<{ job: JobRow }> {
	const res = await fetch(`${BASE}/jobs`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<{ job: JobRow }>(res);
}

export const awsS3DataExportIntegrationQueryKeys = {
	all: ["aws-s3-data-export-integration"] as const,
	destinations: () =>
		[...awsS3DataExportIntegrationQueryKeys.all, "destinations"] as const,
	jobs: (status?: string) =>
		[
			...awsS3DataExportIntegrationQueryKeys.all,
			"jobs",
			status ?? "all",
		] as const,
};
